import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  $cardListStore,
  $initialAccountsStore,
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsGetFxRateStore,
  $transferRequestsUpdateStore,
} from '@lib/store';
import { AmountUtils, cn, PaletteUtils, PaymentUtils, RoutePaths } from '@shared/utils';
import { type AccountResponse, AccountType, AmountTypeEnum, TransferRecipientTypeEnum, TransferRequestTypeEnum } from '@shared/api';
import { FilesFormBlock, FilesFormData, PageContainer, PaymentFormBlock, PaymentFormData, SelectAccount, TransferConfirmationBlock } from '@widgets';
import { Icon, Button, Text, Spinner } from '@shared/widgets';
import { Constants } from '@shared/constants';
import ArrowIcon from '@assets/icons/arrow.svg';
import ArrowVCFCIcon from '@assets/icons/arrow-vcfc.svg';

type SettlementCardFormData = { accountFrom?: AccountResponse; accountTo?: AccountResponse } & PaymentFormData & FilesFormData;
const DEFAULT_VALUES: SettlementCardFormData = {
  accountFrom: undefined,
  accountTo: undefined,
  amountType: AmountTypeEnum.BeneficiaryAmount,
  amount: undefined,
  beneficiaryAmount: undefined,
  paymentReason: '',
  purpose: '',
  files: [],
};

export const PaymentCardSettlementPage = () => {
  const accountStore = useUnit($initialAccountsStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferRequestsUpdateStore = useUnit($transferRequestsUpdateStore.store);
  const transferReasonsStore = useUnit($transferReasonsStore.store);

  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);

  const { t } = useTranslation('payments');
  const formId = useId();
  const formControl = useForm<SettlementCardFormData>({ defaultValues: DEFAULT_VALUES });
  const { control, formState, handleSubmit, watch, setValue } = formControl;
  const accountFrom = watch('accountFrom');
  const accountTo = watch('accountTo');
  const amount = watch('amount');
  const beneficiaryAmount = watch('beneficiaryAmount');

  useEffect(() => {
    Promise.allSettled([$transferReasonsStore.events.getTransferReasons(), $cardListStore.events.getCards({ Count: 100 })]);

    return () => {
      $cardListStore.events.clear();
      $transferRequestsCreateStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (accountStore.data.length === 0) return;
    setValue('accountFrom', accountStore.data[0]);
  }, [accountStore.data]);

  useEffect(() => {
    if (!accountFrom || !accountTo) return;
    if (accountFrom.currency === accountTo.currency) {
      $transferRequestsGetFxRateStore.events.setRate(1);
      return;
    }
    $transferRequestsGetFxRateStore.events.getFxRate({
      AccountId: accountFrom.id,
      FromCurrency: accountFrom.currency,
      ToCurrency: accountTo.currency,
      CountryCode: 'DE',
    });
  }, [accountTo?.currency, accountFrom?.currency]);

  const requestFeeHandler = useCallback(() => {
    if (!amount || !accountTo) return;
    $transferRequestsFeeStore.events.getFee({
      AccountId: accountFrom?.id,
      SenderAmount: amount,
      FastPayment: true,
      BeneficiaryCurrencyCode: accountTo?.currency,
      BeneficiaryBankCountryCode: 'US',
      TransferRequestType: TransferRequestTypeEnum.CardAccountSettlement,
    });
  }, [accountFrom?.id, accountTo?.currency, amount]);
  useEffect(() => {
    requestFeeHandler();
    return () => $transferRequestsFeeStore.events.clear();
  }, [accountFrom?.id, accountTo]);

  const submitHandler = async (data: SettlementCardFormData) => {
    if (!data.accountFrom || !data.accountTo) return;

    if (transferRequestsCreateStore?.data) {
      await $transferRequestsUpdateStore.events.updateTransfer({
        id: transferRequestsCreateStore.data.id,
        type: TransferRequestTypeEnum.CardAccountSettlement,
        fromAccountId: data.accountFrom.id,
        beneficiaryCurrency: data.accountFrom.currency,
        purpose: data.purpose,
        beneficiaryAccountId: data.accountTo.id,
        senderAmount: data.amount!,
        totalAmount: data.amount!,
        beneficiaryAmount: data.beneficiaryAmount!,
        reasonOfThePayment: data.paymentReason,
        beneficiaryCountryCode: 'US',
        fastPayment: true,
        StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
      });
      if (transferRequestsCreateStore.data?.id && data.files?.length) {
        const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transferRequestsCreateStore.data.id, files: data.files });
        setValue('files', files);
      }
      setShowPaymentModal(true);
      return;
    }

    const newTransferRequest = await $transferRequestsCreateStore.events.createTransfer({
      type: TransferRequestTypeEnum.CardAccountSettlement,
      fromAccountId: data.accountFrom.id,
      beneficiaryCurrency: data.accountFrom.currency,
      purpose: data.purpose,
      beneficiaryAccountId: data.accountTo.id,
      senderAmount: data.amount!,
      totalAmount: data.amount!,
      beneficiaryAmount: data.beneficiaryAmount!,
      reasonOfThePayment: data.paymentReason,
      fastPayment: true,
      StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
    });
    if (newTransferRequest.data?.id && data.files?.length) {
      const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: newTransferRequest.data.id, files: data.files });
      setValue('files', files);
    }
    setShowPaymentModal(true);
  };

  const reasonOptions = useMemo(() => {
    const bankCountryCode = Constants.BANK_COUNTRY_CODE;
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Business));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data]);
  return (
    <PageContainer
      title={t('title')}
      header={
        <Link className='group flex items-center gap-2' to={RoutePaths.payments.onNavigate()}>
          <Icon
            icon={PaletteUtils.isInsigniaPalette ? <ArrowIcon /> : <ArrowVCFCIcon className='text-black dark:text-white' />}
            className={cn(PaletteUtils.isInsigniaPalette && 'rotate-180')}
          />
          {PaletteUtils.isInsigniaPalette ? (
            <Text weight='semibold' size='2xl'>
              {t('header')}
            </Text>
          ) : (
            <Text className='group-hover:underline' size='sm'>
              {t('return')}
            </Text>
          )}
        </Link>
      }
      return
    >
      <Spinner show={formState.isSubmitting} position />
      <FormProvider {...formControl}>
        <div className={cn('flex flex-col', PaletteUtils.isVcfcPalette && 'rounded-xl bg-white py-6 dark:bg-soft-black')}>
          <Text
            className={cn(PaletteUtils.isVcfcPalette && 'px-6 text-[26px]')}
            weight={PaletteUtils.isInsigniaPalette ? 'semibold' : undefined}
            size='2xl'
          >
            {t('cardSettlement.title')}
          </Text>
          <form id={formId} noValidate onSubmit={handleSubmit(submitHandler)}>
            <Controller
              name='accountFrom'
              control={control}
              rules={{ required: t('errors.form.required', { ns: 'common' }) }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <SelectAccount
                  className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}
                  label={t('cardSettlement.placeholders.transferFrom')}
                  loading={accountStore.loading}
                  type={AccountType.Payment}
                  accounts={accountStore.data}
                  error={error?.message}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <div className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}>
              <Controller
                name='accountTo'
                control={control}
                rules={{ required: t('errors.form.required', { ns: 'common' }) }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <SelectAccount
                    label={t('cardSettlement.placeholders.selectCardAccount')}
                    loading={accountStore.loading}
                    type={AccountType.Card}
                    accounts={accountStore.data.filter((a) => a.currency === 'EUR').filter((acc) => acc.id !== accountFrom?.id)}
                    error={error?.message}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <PaymentFormBlock
              className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}
              account={accountFrom}
              accountTo={accountTo}
              currency={accountFrom?.currency}
              reasonOptions={reasonOptions}
              onRequestFee={requestFeeHandler}
            />
            <FilesFormBlock
              className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0 pt-0')}
              transactionId={transferRequestsCreateStore.data?.id}
            />
          </form>
          <Button
            className={cn('mt-3 max-w-72', PaletteUtils.isVcfcPalette && 'mt-0 w-[285px] px-6')}
            color='primary'
            type='submit'
            form={formId}
            disabled={feeStore.loading || formState.isSubmitting || transferRequestsCreateStore.loading || transferRequestsUpdateStore.loading}
          >
            {t('form.buttons.transfer')}{' '}
            {beneficiaryAmount! > 0 && `${AmountUtils.transformToAmountWithCurrency(beneficiaryAmount!, accountTo?.currency as string)}`}
          </Button>
        </div>
        {transferRequestsCreateStore.data?.id && (
          <TransferConfirmationBlock
            showPaymentModal={showPaymentModal}
            onClosePaymentModal={() => setShowPaymentModal(false)}
            account={accountFrom!}
            accountTo={accountTo}
          />
        )}
      </FormProvider>
    </PageContainer>
  );
};
